import React from 'react';
import {
  Box, Container, Heading, Text, Flex, useColorModeValue, 
  Icon, SimpleGrid, Stack, Image, Badge, Button, VStack, Divider, 
  List, ListItem, ListIcon, Modal, ModalOverlay, ModalContent, 
  ModalHeader, ModalBody, ModalCloseButton, useDisclosure
} from "@chakra-ui/react";
import { FaGraduationCap } from 'react-icons/fa';
import { GiAbacus } from 'react-icons/gi';
import { FcCollaboration } from 'react-icons/fc';
import { SiBoost } from 'react-icons/si';
import { ImRocket } from 'react-icons/im';
import { BsGraphUp } from 'react-icons/bs'; 
import { FiCalendar, FiCheckCircle } from 'react-icons/fi'; 
import { motion } from 'framer-motion';

import WorkshopIllustrations from '../Underlines/WorkshopsIllustrations';
import AnimatedUnderline1 from '../Underlines/AnimatedUnderline1';
import workshopimg1 from "../../assets/images/workshop1.png"

const MotionBox = motion(Box);

const ServiceCard = ({ icon, title, description }) => {
  const cardBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");

  return (
    <VStack
      bg={cardBg}
      p={8}
      borderRadius="3xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      spacing={4}
      textAlign="center"
      transition="all 0.3s"
      _hover={{ transform: "translateY(-10px)", boxShadow: "2xl" }}
    >
      <Box p={4} borderRadius="2xl" bg="orange.50" color="orange.400">
        <Icon as={icon} boxSize={8} />
      </Box>
      <Heading size="md" fontFamily="Bricolage Grotesque">{title}</Heading>
      <Text fontSize="sm" color="gray.500" lineHeight="tall">
        {description}
      </Text>
    </VStack>
  );
};


const RazorpayButton = () => {
  const formRef = React.useRef(null);

  React.useEffect(() => {
    if (formRef.current && !formRef.current.querySelector('script')) {
      const script = document.createElement('script');
      script.src = "https://checkout.razorpay.com/v1/payment-button.js";
      script.async = true;
      script.setAttribute('data-payment_button_id', 'pl_TGr7pbfUT1g0cp');
      formRef.current.appendChild(script);
    }
  }, []);

  return <form ref={formRef}></form>;
};


const TopicsModal = ({ isOpen, onClose, topics }) => {
  const modalBg = useColorModeValue("white", "gray.900");

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent bg={modalBg} borderRadius="2xl" mx={4}>
        <ModalHeader fontFamily="Bricolage Grotesque">Topics Covered</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <List spacing={3}>
            {topics.map((item, idx) => (
              <ListItem key={idx} fontSize="sm" color="gray.500" display="flex" alignItems="flex-start">
                <ListIcon as={FiCheckCircle} color="orange.400" mt={1} />
                {item}
              </ListItem>
            ))}
          </List>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const EventCard = ({ 
  type, title, subtitle, date, eligibility, description, image,
  workshopDetails, topics, customCta 
}) => {
  // All useColorModeValue calls are declared unconditionally at the top,
  // in the same order every render, regardless of which optional props are passed.
  const cardBg = useColorModeValue("white", "gray.900");
  const cardBorderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const sectionLabelColor = useColorModeValue("gray.700", "gray.300");

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <MotionBox
      whileHover={{ y: -10 }}
      bg={cardBg}
      borderRadius="3xl"
      overflow="hidden"
      boxShadow="2xl"
      border="1px solid"
      borderColor={cardBorderColor}
      h="full"
      display="flex"
      flexDirection="column"
    >
      <Box position="relative">
        <Image src={image} h="240px" w="full" objectFit="cover" />
        <Badge
          position="absolute"
          top={4}
          left={4}
          px={4}
          py={1}
          borderRadius="full"
          colorScheme={type === "Workshop" ? "orange" : "purple"}
          fontSize="xs"
        >
          {type}
        </Badge>
      </Box>

      <Stack p={6} spacing={4} flex="1" display="flex" flexDirection="column">
        <VStack align="flex-start" spacing={1}>
          <Flex align="center" gap={2} color="orange.400">
            <Icon as={FiCalendar} /> {/* FIXED ICON SET */}
            <Text fontSize="xs" fontWeight="bold">{date}</Text>
          </Flex>
          <Heading size="md" fontFamily="Bricolage Grotesque" noOfLines={1}>
            {title}
          </Heading>
          {subtitle && (
            <Text fontSize="xs" fontStyle="italic" color="gray.500">
              {subtitle}
            </Text>
          )}
        </VStack>

        {eligibility && (
          <Text fontSize="xs" fontWeight="black" color="blue.500" textTransform="uppercase">
            {eligibility}
          </Text>
        )}

        <Divider />

        <Text fontSize="sm" color="gray.500" noOfLines={4}>
          {description}
        </Text>

        {workshopDetails && workshopDetails.length > 0 && (
          <>
            <Divider />
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xs" fontWeight="bold" color={sectionLabelColor} textTransform="uppercase">
                Workshop Details
              </Text>
              <List spacing={1}>
                {workshopDetails.map((item, idx) => (
                  <ListItem key={idx} fontSize="xs" color="gray.500">
                    {item}
                  </ListItem>
                ))}
              </List>
            </VStack>
          </>
        )}

        {topics && topics.length > 0 && (
          <>
            <Divider />
            <Button
              onClick={onOpen}
              variant="ghost"
              colorScheme="orange"
              size="sm"
              borderRadius="xl"
              justifyContent="flex-start"
              px={0}
              _hover={{ bg: "transparent", textDecoration: "underline" }}
            >
              View Topics Covered →
            </Button>
            <TopicsModal isOpen={isOpen} onClose={onClose} topics={topics} />
          </>
        )}

        {/* Spacer pushes the CTA to the bottom so all cards align evenly */}
        <Box flex="1" />

        <Divider />

        <Flex justify="center" pt={2}>
          {customCta ? (
            customCta
          ) : (
            <Button 
              colorScheme={type === "Workshop" ? "orange" : "purple"} 
              variant="outline" 
              borderRadius="xl" 
              size="sm" 
              w="full"
            >
              Registrations Closed
            </Button>
          )}
        </Flex>
      </Stack>
    </MotionBox>
  );
};

function Services() {
  const headingColor = useColorModeValue("black", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  const services = [
    { icon: FaGraduationCap, title: "Skill Development", description: "Acquire and refine skills through hands-on opportunities to learn new technologies while honing problem-solving abilities." },
    { icon: GiAbacus, title: "Innovation Catalyst", description: "Breeding grounds for groundbreaking ideas. We encourage participants to think outside the box and solve real problems." },
    { icon: FcCollaboration, title: "Networking", description: "Interact with like-minded individuals from diverse backgrounds. Your next mentor might just be one hackathon away." },
    { icon: ImRocket, title: "Resume Boost", description: "Employers value initiative. Participation in these high-stakes events sets you apart from the competition instantly." },
    { icon: SiBoost, title: "Test-Drive Knowledge", description: "Apply theory in a time-bound, competitive environment. It's an eye-opening experience that pushes your potential." },
    { icon: BsGraphUp, title: "Personal Growth", description: "Build resilience, enhance adaptability, and boost your confidence through challenges that mirror the real tech world." },
  ];

  return (
    <Box py={20}>
      <Container maxW="7xl">
        <VStack spacing={6} mb={16}>
          <Text
            fontSize={["4xl", "5xl", "8xl"]}
            fontWeight="900"
            lineHeight="0.9"
            color={headingColor}
            fontFamily="Bricolage Grotesque"
            textAlign="center"
            letterSpacing="tighter"
          >
            Workshops & <br />
            <span style={{ color: "#F59E0B" }}>Hackathons.</span>
          </Text>
          <AnimatedUnderline1 />
          <Box w="full" maxW="800px">
             <WorkshopIllustrations />
          </Box>
        </VStack>

        <Flex direction={{ base: "column", lg: "row" }} gap={12} align="center" mb={20}>
          <Box flex="1">
            <Heading size="xl" mb={6} fontFamily="Bricolage Grotesque">
              Why these sessions <br /> matter for your career.
            </Heading>
            <Text fontSize="lg" color={subTextColor} lineHeight="tall">
              In the fast-paced world of technology, staying ahead is essential. At GrapplTech, 
              we don't just teach—we foster creativity and enhance skill sets. Our workshops 
              and hackathons are the ultimate playground for professionals of tomorrow.
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} flex="2">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </SimpleGrid>
        </Flex>

        <Box pt={20}>
          <VStack spacing={4} mb={12}>
            <Heading size="2xl" fontFamily="Bricolage Grotesque">Join the Next Sprint</Heading>
            <AnimatedUnderline1 />
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10} alignItems="stretch">
            <EventCard 
              type="Workshop"
              title="Binary Search Blueprint"
              subtitle="Learn Once. Solve Every Binary Search Problem."
              date="August 2, 2026"
              image={workshopimg1}
              description="Master Binary Search in just 3 hours by learning the exact templates, patterns, tricks, and constraint-analysis techniques used in coding interviews. In this session, we'll work through and solve the main set of interview-style Binary Search questions together, using a practical, template-based approach."
              workshopDetails={[
                "📅 Date: August 2, 2026",
                "🕖 Time: 7:00 PM – 10:00 PM (IST)",
                "⏳ Duration: 3 Hours",
                "🌐 Language: Telugu + English",
                "🎓 Certificate: Provided on completion",
              ]}
              topics={[
                "Binary Search Fundamentals",
                "Binary Search Templates (Universal Approach)",
                "Common Problem-Solving Patterns",
                "Interview Tricks & Pattern Recognition",
                "Constraint Analysis & Edge Cases",
                "Live solving of the main set of practice questions",
              ]}
              customCta={<RazorpayButton />}
            />
            <EventCard 
              type="Workshop"
              title="Navigating the Job Market"
              date="August 18, 2026"
              image="https://img.freepik.com/free-vector/business-leader-standing-arrow-holding-flag-flat-vector-illustration-cartoon-people-training-doing-business-plan-leadership-victory-challenge-concept_74855-9812.jpg"
              description="Discover strategies to succeed in your job search. We explore networking, the hidden job market, and building a personalized search plan."
            />
            <EventCard 
              type="Hackathon"
              title="Emerging Tech Hack"
              date="August 14, 2026"
              eligibility="Exclusive for Top Grapplers"
              image="https://img.freepik.com/free-vector/coding-concept-illustration_114360-4348.jpg"
              description="Dive into AI, Blockchain, and IoT. This sprint equips you with the knowledge to adapt and thrive in a rapidly changing tech landscape."
            />
          </SimpleGrid>
        </Box>
      </Container>
    </Box>
  );
}

export default Services;